import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ResolveFn, Router } from '@angular/router';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { StorageKey, StorageService } from '../../core/services/storage.service';

/**
 * CFD-1 — Customer Facing Display launch types.
 *
 * A single attractor media item (image or video) reused from the kiosk
 * attractor playlist shape. Images cycle by `durationSeconds`; videos play
 * to their natural end. `audioEnabled` only applies to video.
 */
export interface CfdAttractorMedia {
  url: string;
  type: 'image' | 'video';
  audioEnabled: boolean;
  durationSeconds?: number | null;
}

/** Store currency formatting, carried in the launch payload because the
 *  display has no tenant login and therefore no populated StoreContext. */
export interface CfdCurrency {
  symbol: string;
  decimalPlaces: number;
  decimalSeparator: string;
  thousandsSeparator: string;
  symbolBeforeAmount: boolean;
}

/** Slim, display-safe store config for the customer screen. Populated from
 *  existing store branding + kiosk attractor media where available; falls
 *  back to logo + name + welcome copy when no media exists. */
export interface CustomerDisplayConfig {
  storeName: string;
  storeLogoUrl: string | null;
  themePrimaryColor: string | null;
  welcomeHeadlineEn: string | null;
  welcomeHeadlineAr: string | null;
  welcomeTaglineEn: string | null;
  welcomeTaglineAr: string | null;
  attractorPlaylist: CfdAttractorMedia[];
  themeId: string | null;
  /** 'split' (cart + persistent media rail) or 'full' (cart full-bleed,
   *  media only when idle). CFD-2A: store-configured. */
  layoutMode: 'split' | 'full';
  idleTimeoutSeconds: number;
  currency: CfdCurrency;
  /** CFD-2A — brand accent hex (e.g. #C41E3A) applied as --cfd-accent.
   *  Null → the display's default red. Additive (older payloads omit it). */
  themeAccentColor?: string | null;
  /** CFD-2A — when false, the split-layout media rail is hidden during the
   *  live cart (cart goes full-width). Defaults true. */
  showMediaRailWhileLive?: boolean;
  /** CFD-6A — per-store kill switch for live cart mirroring. Absent (older
   *  payloads) or true = mirror the cashier's in-progress cart; false = the
   *  display only updates on save / payment / void, as it did pre-CFD-6. */
  liveCartEnabled?: boolean;
}

/** Full launch response from POST /api/public/store/{storeCode}/customer-display-launch.
 *  Stored verbatim in StorageKey.CFD_LAUNCH so the display shell reads
 *  storeId / tenantId / config directly without a follow-up call. */
export interface CustomerDisplayLaunchResponse {
  token: string;
  storeId: string;
  tenantId: string;
  storeName: string;
  posStoreCode: string;
  expiresAt: string;
  config: CustomerDisplayConfig;
}

interface CfdLaunchEnvelope {
  success: boolean;
  data?: CustomerDisplayLaunchResponse;
  error?: string;
}

/**
 * Frontend counterpart of the backend CustomerDisplay launch flow. The POS
 * SPA route `/customer-display/s/{storeCode}` calls `launch(storeCode)` via
 * its resolver; the service POSTs to the public endpoint, stores both the
 * cfdToken and the full launch payload in localStorage, and resolves with
 * the payload so the resolver can hand it straight to the shell.
 *
 * Trust model — Path A: storeCode IS the credential (matches kiosk + KDS +
 * pickup-display). The minted cfdToken is least-privilege (role
 * CustomerDisplay, read-only): the auth interceptor's `isCfdApi` branch
 * attaches it ONLY to the trimmed check-read endpoint. Token TTL is 12h; on
 * 401 the shell re-launches to mint a fresh one.
 *
 * `providedIn: 'root'` because there is exactly one in-flight CFD session
 * per tab.
 */
@Injectable({ providedIn: 'root' })
export class CustomerDisplayLaunchService {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(StorageService);
  private readonly baseUrl = environment.apiUrl;

  /**
   * Mint a cfdToken for the given storeCode. Persists both the token and
   * the full launch payload to localStorage before resolving.
   *
   * Returns `null` on any failure (network error, 404, 5xx) — the resolver
   * treats null as "redirect to /customer-display-unavailable". The error
   * reason is logged but not surfaced because the BE returns uniform 404s
   * for anti-enumeration.
   */
  launch(storeCode: string): Observable<CustomerDisplayLaunchResponse | null> {
    if (!storeCode || storeCode.length > 32) {
      return of(null);
    }
    // POST (matches BE) — the launch produces a fresh JWT each call and
    // must never be cached.
    const url = `${this.baseUrl}/public/store/${encodeURIComponent(storeCode)}/customer-display-launch`;
    return this.http.post<CfdLaunchEnvelope>(url, {}).pipe(
      map(res => {
        if (!res.success || !res.data) {
          console.warn('[CustomerDisplayLaunchService] launch failed:', res.error);
          return null;
        }
        return res.data;
      }),
      tap(data => {
        if (data) {
          // Persist token + payload. The interceptor reads CFD_TOKEN to
          // attach the bearer on the check-read endpoint; the shell reads
          // CFD_LAUNCH for storeId/tenantId/config.
          this.storage.setRaw(StorageKey.CFD_TOKEN, data.token);
          this.storage.set(StorageKey.CFD_LAUNCH, data);
        }
      }),
      catchError(err => {
        console.error('[CustomerDisplayLaunchService] launch error:', err);
        return of(null);
      })
    );
  }

  /** Read the cached launch payload from localStorage. Null if no launch
   *  has happened in this tab yet (or storage was cleared). */
  getCachedLaunch(): CustomerDisplayLaunchResponse | null {
    return this.storage.get<CustomerDisplayLaunchResponse>(StorageKey.CFD_LAUNCH);
  }

  /** Drop the cached token + payload. Called on 401 (token expired) before
   *  a re-launch. */
  clear(): void {
    this.storage.remove(StorageKey.CFD_TOKEN);
    this.storage.remove(StorageKey.CFD_LAUNCH);
  }
}

/**
 * Angular route resolver for `/customer-display/s/{storeCode}`. Fires before
 * navigation completes, so the shell never mounts without a valid cfdToken
 * in storage. On failure (unknown storeCode / inactive store / network down)
 * it redirects to `/customer-display-unavailable` — matching the uniform-404
 * anti-enumeration posture of the BE.
 *
 * Always fires a fresh launch (does NOT reuse a cached payload) so the
 * cfdToken TTL resets on each entry and branding/config stays current.
 */
export const cfdLaunchResolver: ResolveFn<CustomerDisplayLaunchResponse | null> = (route) => {
  const launcher = inject(CustomerDisplayLaunchService);
  const router = inject(Router);

  const storeCode = route.paramMap.get('storeCode') ?? '';

  return launcher.launch(storeCode).pipe(
    tap(result => {
      if (!result) {
        void router.navigateByUrl('/customer-display-unavailable');
      }
    })
  );
};
